import React, { useEffect, useState } from "react";
import { Box, Button, useTheme, Stack, Tooltip, TextField, FormControl, Tab, Tabs, Chip } from "@mui/material";
import AxionControls from "../../../components/controls/AxionControls";
import dayjs from "dayjs";

import messages from "./messages";
import { tokens } from "../../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { reportProductionOrderInSystem, getProductionOrderByOrderNo } from "../../../fetchers/productionOrder";
import { fetchProductionItemByItemNo } from "../../../fetchers/productionItems";
import { useQuery } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import useFeatureFlags, { isFeatureEnabled } from '../../../hooks/useFeatureFlags';
import { ShowerRounded } from "@mui/icons-material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useMsal, useAccount } from "@azure/msal-react";
import { fetchUsers } from "../../../fetchers/user";
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

const StyledTabs = styled((props) => (
    <Tabs
        {...props}
        TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    />
))({
    '& .MuiTabs-indicator': {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
    '& .MuiTabs-indicatorSpan': {
        width: '100%',
        backgroundColor: '#fff',
    },
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(

    ({ theme }) => ({

        textTransform: 'none',
        fontWeight: theme.typography.fontWeightRegular,
        fontSize: theme.typography.pxToRem(20),
        marginRight: theme.spacing(0),
        minWidth: "150px",
        //backgroundColor: '#799c4a',
        color: 'rgba(255, 255, 255, 0.7)',
        '&.Mui-selected': {
            color: '#fff',
            backgroundColor: "#434957 !important",
        },
        '&.Mui-focusVisible': {
            backgroundColor: 'rgba(100, 95, 228, 0.32)',
        },
        '&.MuiTab-root': {
            backgroundColor: '#43495799',
            border: "1px solid #0c101b",
        },
    }),
);

export default function ProductionOrderReport(props) {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [buttonDisabled, setButtonDisabled] = React.useState(false);
    const [ongoingPO, setOngoingPO] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [oldReportedQuantity, setOldReportedQuantity] = useState(0);
    const [currentReportedQTY, setCurrentReportedQTY] = useState(0);
    const [addQuantityInput, setAddQuantityInput] = useState("0");
    const [totalReportedQTY, setTotalReportedQTY] = useState(0);
    const [remainingQTY, setRemainingQTY] = useState(0);
    const [notReportedQty, setNotReportedQty] = useState(0);
    const [producedQTY, setProducedQTY] = useState(0);
    const [sign, setSign] = useLocalStorage("oee_report_sign", "");
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const [closers, setClosers] = useState([]);

    const machineId = props.machineId;
    //const machineInfo = props.machineInfo;


    //console.log("[Debug] ProductionOrderReport props.productionOrder: ", props.productionOrder);

    //console.log("[Debug] Not reported quantity: ", (props.machine));

    //console.log("[Debug] Not reported quantity: ", props.producedByPO);

    const [action, setAction] = useState("Report"); // "Start" or "Report" or "Stop" or "Edit"

    const productionOrderQuery = useQuery(
        ["productionordertransactions", companyKey, props.productionOrder],
        () => getProductionOrderByOrderNo(props.productionOrder?.productionOrderNo, props.productionOrder?.operationNumber, companyKey),
        {}
    );

    const usersQuery = useQuery(
        ["allUsers"],
        () => fetchUsers(),
        {
            staleTime: 300000,
            keepPreviousData: true
        }
    );

    const productionItemQuery = useQuery(
        ["productionitem", props.itemNo],
        () => fetchProductionItemByItemNo(props.itemNo),
        {}
    );

    useEffect(() => {
        if (!usersQuery?.data || usersQuery.isLoading) {
            return;
        }
        setClosers([{ id: account.username, title: `Inloggad användare (${account.username})` }, ...usersQuery.data?.map((user) => ({ id: user.username, title: `${user.name} (${user.username})` }))]);
    }, [usersQuery.data]);

    useEffect(() => {
        setAction("Report");
    }, [props.action]);

    useEffect(() => {
        //calcRemainingQuantity(productionOrderQuery.data?.plannedQTY, productionOrderQuery.data?.reportedQTY);
        const reported = Number(productionOrderQuery.data?.reportedQTY) || 0;
        setTotalReportedQTY(reported);
        calcRemainingQuantity(productionOrderQuery.data?.plannedQTY, reported);
        setProducedQTY(props.producedByPO.sumCavities || 0);
        setNotReportedQty((props.producedByPO.sumCavities || 0) - reported);
    }, [productionOrderQuery.data?.plannedQTY, productionOrderQuery.data?.reportedQTY]);

    useEffect(() => {
        setOldReportedQuantity(props.productionOrder?.reportedQTY || 0);
        //console.log("[Debug] ProductionOrderReport props.productionOrder: ", props.productionOrder);
    }, []);

    const setShowBackdrop = (show) => {
        if (props.setShowBackdrop) {
            props.setShowBackdrop(show);
        }
    }

    const handleRefreshData = () => {
        if (props.handleRefreshData) {
            props.handleRefreshData();
        }
    }

    function calcRemainingQuantity(planned, reported) {
        const plannedNum = parseFloat(planned);
        const reportedNum = parseFloat(reported);
        if (isNaN(plannedNum) || isNaN(reportedNum)) {
            return null;
        }
        const remaining = plannedNum - reportedNum;
        // if (remaining < 0) {
        //     return 0;
        // }

        setRemainingQTY(remaining);
    }

    const handleAddQtyChange = (event) => {
        const { name, value } = event.target;

        //console.log("[Debug] handleAddQtyChange qty", value , isNaN(value));

        // Typing "-" after a lone "0" appends to the end (e.g. "0" + "-" => "0-"); treat that as just "-"
        const value_ = /^0+-$/.test(value) ? "-" : value;

        // Only allow digits, an optional leading "-" and a decimal point (e.g. "-", "-5", "5.5")
        if (!/^-?\d*\.?\d*$/.test(value_)) {
            return;
        }

        // Strip a leading "0" (e.g. typing "5" while the field shows "0" becomes "5", not "05")
        const normalizedValue = value_.replace(/^(-?)0+(\d)/, "$1$2");

        // Keep the raw text as-is so intermediate states like "-" or "-5." while typing a negative number aren't wiped out
        setAddQuantityInput(normalizedValue);

        let oldReportedQty = productionOrderQuery.data?.reportedQTY;
        if (isNaN(oldReportedQty)) {
            oldReportedQty = 0;
        }

        // value can be a decimal number, so we need to parse it as a float
        let parsedValue = parseFloat(normalizedValue);
        if (isNaN(parsedValue)) {
            parsedValue = 0;
        }

        let totalReported = parsedValue + parseFloat(oldReportedQty);
        if (isNaN(totalReported)) {
            totalReported = 0;
        }

        setCurrentReportedQTY(parsedValue);
        setTotalReportedQTY(totalReported);
        calcRemainingQuantity(productionOrderQuery.data?.plannedQTY, totalReported)
    }

    const handleAddQtyBlur = (event) => {
        const { value } = event.target;

        if (value === "" || isNaN(parseFloat(value))) {
            setAddQuantityInput("0");
        }
    }

    const handleAddQty = (qty) => {

        //console.log("[Debug] handleAddQty qty", qty);

        let reportedQty = productionOrderQuery.data?.reportedQTY;
        if (isNaN(reportedQty)) {
            reportedQty = 0;
        }

        let current = currentReportedQTY + qty;
        //Allow negative reporting
        // if (current < 0) {
        //     current = 0;
        // }

        //How much is reported right now
        setCurrentReportedQTY(current);
        setAddQuantityInput(current.toString());
        setTotalReportedQTY(current + reportedQty);
        calcRemainingQuantity(productionOrderQuery.data?.plannedQTY, current + reportedQty)
    }

    const handleUpdatePO = () => {
        setButtonDisabled(true);
        setShowBackdrop(true);
        updateActivePO();
    }

    // const buildOrderObject = () => {

    //     let order = {
    //         erpId: "x",
    //         companyKey: companyKey.toString(),
    //         machineId: machineId,
    //         activeProductionOrderNumber: props.productionOrder,
    //         activeTool: productionOrderQuery.data?.toolId?.toString() || "",
    //         idealCycleTime: productionOrderQuery.data?.idealCycleTime,
    //         plannedQuantity: productionOrderQuery.data?.plannedQTY,
    //         reportedQuantity: productionOrderQuery.data?.reportedQTY,
    //         remainingQuantity: remainingQTY,
    //         activeItemId: productionOrderQuery.data?.itemNo,
    //         cavities: productionOrderQuery.data?.cavities,
    //         activeProductionOrderIndex: 0,
    //         updatedAt: new Date().toISOString(),
    //     };

    //     return order;
    // }

    const buildOrderObject = () => {

        let order = { ...props.productionOrder, remainingQTY: remainingQTY };

        return order;
    }


    const updateActivePO = () => {

        //Validate input
        if (isNaN(currentReportedQTY) || currentReportedQTY === 0) {
            pushErrorMessage(formatMessage(messages.po_message_invalidqty));
            setShowBackdrop(false);
            setButtonDisabled(false);
            return;
        }


        let orderToUpdate = buildOrderObject();

        orderToUpdate.reportedQTY = totalReportedQTY;
        orderToUpdate.remainingQTY = remainingQTY;// - currentReportedQTY;

        let qty = currentReportedQTY.toString().replace(",", ".");

        // console.log("Updating active production order: ", orderToUpdate);
        // console.log("Updating order: ", selectedTab, orderToUpdate);

        // console.log("Updating active production order first: ", orderToUpdate);
        let status = orderToUpdate.statusId; // 2:Running

        reportProductionOrderInSystem(orderToUpdate, status, qty, sign).then((result) => {

            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);

            pushNotification(formatMessage(messages.po_message_updated));

            handleRefreshData();
            if (props.invalidateMachineInfo) {
                props.invalidateMachineInfo();
            }
        }).catch((error) => {
            console.log("Could not update active production order", error);
            pushErrorMessage(formatMessage(messages.po_message_updated_error) + ": " + error?.message);
            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);
        });
    }

    const getUserNameById = (userId) => {
        let user = usersQuery.data?.find(user => user.id === userId);
        user = user?.username?.split("@")[0];
        user = user?.replace(".", " ");
        user = user?.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
        return user || "";
    }


    const columns = [
        {
            field: "productionOrderNo",
            headerName: formatMessage(messages.po_report_table_productionorderno),
            width: 100
        },
        {
            field: "operationNumber",
            headerName: formatMessage(messages.po_report_table_operationnumber),
            width: 100
        },
        {
            field: "qty",
            headerName: formatMessage(messages.po_report_table_qty),
            headerAlign: "right",
            align: "right",
            width: 100
        },
        {
            field: "reportedQTY",
            headerName: formatMessage(messages.po_report_table_reportedqty),
            headerAlign: "right",
            align: "right",
            width: 100
        },

        {
            field: "userId",
            headerName: formatMessage(messages.po_report_table_user),
            width: 150,
            valueGetter: (params) => getUserNameById(params.row.userId)
        },
        {
            field: "updated",
            headerName: formatMessage(messages.po_report_table_updated),
            width: 150,
            valueGetter: (params) => params.row.updated ? params.row.updated.toString().replace("T", " ").replace("Z", "") : ""
        },
        {
            field: "sentToErp",
            headerName: "Integration",
            width: 150,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                if (!params.row.sentToErp) {
                    if (dayjs().diff(dayjs(params.row.updated), 'minute') < 5) {
                        return (
                            <Tooltip title={formatMessage(messages.po_report_tooltip_waiting_for_integration)} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                                <ScheduleOutlinedIcon />
                            </Tooltip>
                        );
                    } else {
                        if (params.row.sentToErpErrorResponse === null || params.row.sentToErpErrorResponse === "") {
                            return (
                                // Increase font size for better readability.
                                <Tooltip title={formatMessage(messages.po_report_tooltip_missing_integration_error)} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                                    <ReportProblemOutlinedIcon />
                                </Tooltip>
                            );
                        } else {
                            return (
                                <Tooltip title={formatMessage(messages.po_report_tooltip_integration_error, { error: params.row.sentToErpErrorResponse })} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                                    <ErrorOutlineOutlinedIcon />
                                </Tooltip>
                            );
                        }
                    }
                } else {
                    return (
                        <Tooltip title={formatMessage(messages.po_report_tooltip_sent_to_erp)} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                            <CheckCircleOutlineOutlinedIcon />
                        </Tooltip>
                    );
                }
            },
        },

    ];

    if (productionOrderQuery === undefined || productionOrderQuery.data === undefined || productionOrderQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };
    if (usersQuery === undefined || usersQuery.data === undefined || usersQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };
    if (productionItemQuery === undefined || productionItemQuery.data === undefined || productionItemQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };


    // Debug logs----------------------------------------------------

    //console.log("[Debug] productionOrderQuery. productionOrderTransactions: ", productionOrderQuery.data?.productionOrderTransactions);
    //console.log("[Debug] usersQuery: ", usersQuery.data);
    //console.log("[Debug] productionItemQuery: ", productionItemQuery);
    //console.log ("[Debug] props: ", props);

    return (
        <>


            <Box width="480px">

                {(ongoingPO !== null && ongoingPO.activeProductionOrderNumber !== undefined && ongoingPO.activeProductionOrderNumber !== null && action === "Start") &&
                    <Box display="flex" justifyContent="center" mb={2} sx={{
                        //display: "grid",
                        //gridTemplateColumns: "repeat(1, 1fr)",
                        gap: 1,
                        padding: "15px",
                        marginTop: "16px",
                        border: "1pt solid #ffffff"
                    }}>
                        <FormattedMessage {...messages.activeProductionOrderRestartMessage} /></Box>
                }

                <Stack direction="row" spacing={2} marginTop={2} sx={{ width: "100%" }}>
                    <Box width="50%">
                        <TextField
                            name="addQuantity"
                            label={formatMessage(messages.po_report_addqty)}
                            value={addQuantityInput}
                            onChange={handleAddQtyChange}
                            onBlur={handleAddQtyBlur}
                            disabled={(action === "Stop" ? true : false) || selectedTab !== 0}
                            autoComplete="off"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Box>



                    <Box width="50%" display="flex" justifyItems="flex-end">
                        {productionItemQuery.data?.batchSize !== null && productionItemQuery.data.batchSize > 0 && (
                            <Button
                                variant="contained"
                                sx={{ marginLeft: "auto", marginTop: 0, width: "150px", height: "52px", backgroundColor: colors.axionGreen[500] }}
                                onClick={() => handleAddQty(productionItemQuery.data?.batchSize)}
                            > {formatMessage(messages.po_report_addbatch)} ({productionItemQuery.data?.batchSize})
                            </Button>
                        )}
                    </Box>

                    {/* Not yet reported qty: {totalScrapedQTY - currentScrapedQTY} */}
                    <Box width="50%" display="flex" justifyItems="flex-end">

                        <Button
                            variant="contained"
                            sx={{ marginLeft: "auto", marginTop: 0, width: "150px", height: "52px", backgroundColor: colors.axionGreen[500] }}
                            onClick={() => handleAddQty(notReportedQty)}
                            disabled={producedQTY === 0}
                        >
                            {formatMessage(messages.po_report_notReported)} ({notReportedQty})
                        </Button>

                    </Box>


                </Stack>

                <Stack direction="row" spacing="auto" marginTop={2} sx={{ width: "100%" }} display="flex">

                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(1)}
                    > +1
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(5)}
                    > +5
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(10)}
                    > +10
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(20)}
                    > +20
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(50)}
                    > +50
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(100)}
                    > +100
                    </Button>
                </Stack>
                <Stack direction="row" spacing="auto" marginTop={2} marginBottom={1} sx={{ width: "100%" }} display="flex">

                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-1)}
                    > -1
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-5)}
                    > -5
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-10)}
                    > -10
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-20)}
                    > -20
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-50)}
                    > -50
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-100)}
                    > -100
                    </Button>
                </Stack>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} marginTop={4}>
                <Box>
                    <AxionControls.AxionSelect
                        label={formatMessage(messages.po_report_sign)}
                        sx={{ width: '480px' }}
                        options={closers}
                        showNoneOption={false}
                        name="activityCloser"
                        value={sign}
                        onChange={(event) => setSign(event.target.value)}
                        searchField={true}
                        searchFieldPlaceholder={formatMessage(messages.po_report_searchuser)}
                    />
                </Box>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="flex-end" marginTop="20px">
                <Button
                    variant="contained"
                    sx={{
                        marginTop: "20px",
                        marginLeft: "auto",
                        display: "block",
                        width: "140px",

                    }}

                    //disabled={((action === "Report" && props.machineInfo?.activeProductionOrderNumber === null) || action === "Stop") ? true : buttonDisabled}
                    onClick={handleUpdatePO}
                >
                    {formatMessage(messages.po_report_btnreport)}
                </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ paddingTop: "30px" }} width="100%">
                <TextField
                    name="plannedQuantity"
                    label={formatMessage(messages.po_report_plannedqty)}
                    value={productionOrderQuery.data?.plannedQTY}
                    autoComplete="off"
                    //onChange={handlePlannedQtyChange}
                    disabled={true}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
                <TextField
                    name="producedQuantity"
                    label={formatMessage(messages.po_report_producedqty)} //{formatMessage(messages.reportedQuantityLabel)}
                    value={producedQTY}
                    autoComplete="off"
                    disabled={true}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
                <TextField
                    name="oldQuantity"
                    label={formatMessage(messages.po_report_reportedqty)} //{formatMessage(messages.reportedQuantityLabel)}
                    value={productionOrderQuery.data?.reportedQTY}
                    autoComplete="off"
                    disabled={true}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
                <TextField
                    name="reportedQuantity"
                    label={formatMessage(messages.po_report_totalreportedqty)} //{formatMessage(messages.reportedQuantityLabel)}
                    value={totalReportedQTY}
                    autoComplete="off"
                    //onChange={handleReportedQtyChange}
                    disabled={true}
                    InputLabelProps={{
                        shrink:
                            totalReportedQTY !== null &&
                            totalReportedQTY !== undefined &&
                            totalReportedQTY !== "",
                    }}
                />
                <TextField
                    name="remainingQuantity"
                    label={formatMessage(messages.po_report_remainingqty)} //{formatMessage(messages.remainingQuantityLabel)}
                    value={remainingQTY}
                    autoComplete="off"
                    //onChange={handleActiveProductionOrderChange}
                    disabled={true}
                    InputLabelProps={{
                        shrink:
                            remainingQTY !== null &&
                            remainingQTY !== undefined &&
                            remainingQTY !== "",
                    }}
                />
            </Stack>

            {productionOrderQuery.data?.productionOrderTransactions?.length > 0 && (
                <>
                    <Box height="300px" marginTop="20px">
                        <DataGridPro
                            //paddingTop="20px"
                            //autoHeight
                            height="300px"
                            key={"GridID"}
                            rows={productionOrderQuery.data.productionOrderTransactions.filter(tx => !tx.reportedBySystem).sort((a, b) => b.created.localeCompare(a.created)) || []}
                            columns={columns}
                            checkboxSelection={false}
                            disableSelectionOnClick={true}
                            //getRowClassName={getRowClassName}
                            sx={{
                                border: '1px solid ' + colors.primary[500],
                                '& .MuiDataGrid-cell': {
                                    fontSize: '14px',
                                    borderBottom: '1px solid ' + colors.primary[500],
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '1px solid ' + colors.primary[500],
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: '16px',
                                    whiteSpace: 'normal',
                                    lineHeight: 1.2,

                                    //fontWeight: 'bold',
                                },
                                '& .active-po-row': {
                                    backgroundColor: colors.axionGreen[500],
                                },

                                '& .active-po-row:hover': {
                                    backgroundColor: colors.axionGreen[600],
                                },
                                '& .paused-po-row': {
                                    backgroundColor: "#C7A84B",
                                },
                                '& .paused-po-row:hover': {
                                    backgroundColor: "#C7A84B",
                                },
                                '& .stopped-po-row': {
                                    backgroundColor: "#C45747",
                                },
                                '& .stopped-po-row:hover': {
                                    backgroundColor: "#B85C4A",
                                },
                            }}
                        />
                    </Box>
                </>
            )}



        </>
    );
}